import React from "react";
import tw, { css } from "twin.macro";
import { type UseFormRegisterReturn } from "react-hook-form";
import { FieldError } from "~/features/ui/form/field-error";
import { Icon } from "~/features/ui/icon";
import Checkmark from "~/public/icons/checkmark.svg";

type Props = {
  label: string;
  error?: string;
  register?: UseFormRegisterReturn;
};

function checkmarkBoxStyles(hoverId?: string) {
  return [
    tw`transition-colors duration-250 flex justify-center items-center m-0 cursor-pointer shrink-0 w-24 h-24 border relative rounded-2`,
    css`
      ${`#${hoverId}`}:hover & {
        ${tw`border-black-100-light`}

        [data-icon] {
          ${tw`opacity-20`}
        }
      }

      input:checked ~ & {
        ${tw`border-black-100-light`}

        [data-icon] {
          ${tw`opacity-100!`}
        }
      }
    `,
  ];
}

export const Checkbox = React.forwardRef<HTMLInputElement, Props & React.ComponentPropsWithRef<"input">>(
  (props, ref) => {
    const { error, label, register, type = "checkbox", ...rest } = props;
    const hoverId = `checkbox-${props.name}`;

    return (
      <div tw="flex flex-col relative">
        <label
          id={hoverId}
          css={[
            tw`transition-colors duration-250`,
            tw`p-16 relative rounded-4 border flex gap-8 cursor-pointer`,
            error ? tw`border-error` : tw` border-black-transparent-20-light`,
          ]}
        >
          <input tw="sr-only" type={type} ref={ref} {...register} {...rest} />

          <span
            css={[
              checkmarkBoxStyles(hoverId),
              Boolean(error) ? tw`border-error` : tw`border-black-transparent-20-light`,
            ]}
          >
            <Icon data-icon icon={Checkmark} tw="transition-opacity duration-250 opacity-0" />
          </span>
          <span tw="text-mobile-body-20-normal lg:text-desktop-body-20-normal">{label}</span>
        </label>
        {error && <FieldError message={error} />}
      </div>
    );
  }
);
